/**
 * ModuleGraph — the blueprint as a settled FORCE GRAPH.
 *
 * The idea is Obsidian's graph view, not its styling: every module is a dot
 * whose size is its degree and whose colour is its executor lane, the edges are
 * springs, and the whole thing is dropped into a little physics sim that pulls
 * related modules together and pushes everything else apart. What you are
 * looking at is therefore the blueprint's real SHAPE — pages as lobes, the
 * wired core between them — rather than an arrangement someone chose.
 *
 * The columns-and-cards layouts this replaced (use flow · dependency layers ·
 * card lanes · the classic port diagram) each answered one question and forced
 * the reader to switch modes to ask the next; and the card grid could not scale
 * past ~25 modules without becoming a 2000px ribbon.
 *
 * TWO EDGE CLASSES, both load-bearing (see graphLayout.js):
 *   connection   the blueprint's `connections[]` — strong, directed, labelled
 *                `signal → slot`, coloured by executor, dashed when async.
 *   containment  parent → child — faint, weak, unlabelled. Without it two
 *                thirds of a big blueprint is unwired dust.
 *
 * The MINIATURE — a real scaled render of the kit component — is still here,
 * but on HOVER only: sixty live components inside a graph is a page that
 * stutters, and the miniature answers "what IS this module", which is a
 * question you ask about one node at a time.
 */
import { useMemo, useState } from 'react';
import { lookupKit } from '../viewir/registry';
import { ViewIRComposition } from '../viewir/interpreter';
import { buildTokenStyle } from '../viewir/tokens';
import { SAMPLES } from './previewSamples';
import { graphModel, settle } from './graphLayout';

const C = {
  ui: '#5856d6', data: '#b45309', host: '#6e6e73',
  ink: '#1d1d1f', dim: '#8e8e93', line: '#e2e2ea', soft: '#d8d8e2', bg: '#fff',
};

/** The hover card's own size — NOT PhoneCard's, and not the old grid's. */
const PEEK_W = 200;
const PEEK_H = 96;
const SCALE = 0.55; // the samples are drawn for a ~330px cell; this fits the peek

/** Room around the settled box for the biggest node plus its label. */
const PAD = 46;

/** A dot's radius, from its degree — capped so a hub cannot swallow the page. */
function radiusOf(degree) {
  return 6 + Math.min(9, Math.sqrt(Math.max(0, degree)) * 3.2);
}

/** A live miniature of one kit component, or an honest placeholder. */
function MiniModule({ type }) {
  const kit = lookupKit(type);
  const sample = SAMPLES[type];
  const style = buildTokenStyle({ appearance: 'light', accent: '#6E59F2' });

  if (!kit?.component && kit?.composition && sample) {
    // A composed (L3 Domain) module has no code of its own — the miniature is
    // its expansion, exactly what the runtime renders.
    return (
      <div style={{
        ...style, height: PEEK_H, borderRadius: 8, overflow: 'hidden',
        position: 'relative', background: 'var(--bg)',
      }}>
        <div style={{
          transform: `scale(${SCALE})`, transformOrigin: 'top left',
          width: PEEK_W / SCALE, height: PEEK_H / SCALE, padding: 8, boxSizing: 'border-box',
          pointerEvents: 'none',
        }}>
          <ViewIRComposition
            type={type} nodeId={`graph-${type}`} def={kit.composed}
            props={sample.props || {}} data={sample.data}
            ctx={sample.ctx || { entries: [], settings: {}, pending: { adds: [] } }}
          />
        </div>
      </div>
    );
  }

  if (!kit?.component || !sample) {
    return (
      <div style={{
        ...style, height: PEEK_H, borderRadius: 8, border: '1px dashed var(--line)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 10.5, color: 'var(--text-3)', textAlign: 'center', padding: 6,
      }}>
        {type === 'host' ? 'host bridge — no UI' : `${type} — no live sample`}
      </div>
    );
  }

  const Component = kit.component;
  return (
    <div style={{
      ...style, height: PEEK_H, borderRadius: 8, overflow: 'hidden',
      position: 'relative', background: 'var(--bg)',
    }}>
      <div style={{
        transform: `scale(${SCALE})`, transformOrigin: 'top left',
        width: PEEK_W / SCALE, height: PEEK_H / SCALE, padding: 8, boxSizing: 'border-box',
        // A miniature is a picture of the module, not a control surface.
        pointerEvents: 'none',
      }}>
        <Component
          nodeId={`graph-${type}`}
          variant={sample.variant}
          props={sample.props || {}}
          data={sample.data}
          ctx={sample.ctx || { entries: [], settings: {}, pending: { adds: [] } }}
          emit={() => {}}
          slots={sample.slots || {}}
          childItems={[]}
          renderTile={sample.renderTile}
          renderById={() => null}
        >
          {sample.children || null}
        </Component>
      </div>
    </div>
  );
}

export default function ModuleGraph({ blueprint }) {
  const [hover, setHover] = useState(null); // a node id
  const [hoverEdge, setHoverEdge] = useState(null); // an edge key

  // ONE settle per blueprint. It is pure and deterministic, so the picture is
  // the same on every render and a re-render never reshuffles the graph.
  const g = useMemo(() => {
    const model = graphModel(blueprint);
    const pos = settle(model.nodes, model.edges);
    const nodes = model.nodes.map((n) => ({
      ...n, r: radiusOf(n.degree), x: (pos[n.id]?.x || 0) + PAD, y: (pos[n.id]?.y || 0) + PAD,
    }));
    const at = new Map(nodes.map((n) => [n.id, n]));
    const edges = model.edges.map((e) => {
      const a = at.get(e.fromId);
      const b = at.get(e.toId);
      if (!a || !b) return null;
      return { ...e, x1: a.x, y1: a.y, x2: b.x, y2: b.y, mx: (a.x + b.x) / 2, my: (a.y + b.y) / 2 };
    }).filter(Boolean);
    return {
      nodes,
      edges,
      connections: edges.filter((e) => e.kind === 'connection').length,
      width: Math.max(320, Math.max(...nodes.map((n) => n.x), 0) + PAD),
      height: Math.max(240, Math.max(...nodes.map((n) => n.y), 0) + PAD),
    };
  }, [blueprint]);

  const edgeColor = (e) => (e.kind === 'containment'
    ? C.soft
    : C[e.executor === 'data' ? 'data' : e.executor === 'host' ? 'host' : 'ui']);
  /** Everything the hovered node touches stays lit; the rest fades back. */
  const lit = (id) => hover === null || hover === id
    || g.edges.some((e) => (e.fromId === hover && e.toId === id) || (e.toId === hover && e.fromId === id));
  const onEdge = (e) => hover === null || e.fromId === hover || e.toId === hover;

  const peek = hover ? g.nodes.find((n) => n.id === hover) : null;
  const shownEdge = hoverEdge ? g.edges.find((e) => e.key === hoverEdge) : null;

  return (
    // A BOUNDED box: a settled graph can be wider than the studio's right
    // region, so it scrolls inside itself rather than pushing the page sideways.
    <div style={{ background: C.bg, border: `1px solid ${C.line}`, borderRadius: 14, maxWidth: '100%', overflow: 'hidden' }}>
      <div data-testid="module-graph-scroll" style={{ overflowX: 'auto', overflowY: 'auto', maxWidth: '100%', maxHeight: 560 }}>
        <div
          data-testid="module-graph-canvas"
          style={{ position: 'relative', width: g.width, height: g.height }}
          onMouseLeave={() => { setHover(null); setHoverEdge(null); }}
        >
          <svg width={g.width} height={g.height} style={{ position: 'absolute', inset: 0 }}>
            <defs>
              <marker id="viForceArrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
                <path d="M 0 0 L 10 5 L 0 10 z" fill={C.dim} />
              </marker>
            </defs>
            {/* CONTAINMENT first, so the wires that carry meaning are on top. */}
            {g.edges.filter((e) => e.kind === 'containment').map((e) => (
              <line
                key={e.key}
                data-edge={e.key} data-edge-kind="containment" data-from={e.fromId} data-to={e.toId}
                x1={e.x1} y1={e.y1} x2={e.x2} y2={e.y2}
                stroke={edgeColor(e)} strokeWidth={onEdge(e) ? 1 : 0.6}
                opacity={onEdge(e) ? 0.85 : 0.12}
              />
            ))}
            {g.edges.filter((e) => e.kind === 'connection').map((e) => (
              <g key={e.key} opacity={onEdge(e) ? 1 : 0.08}>
                <line
                  data-edge={e.key} data-edge-kind="connection" data-from={e.fromId} data-to={e.toId}
                  data-signal={e.signal} data-slot={e.slot}
                  x1={e.x1} y1={e.y1} x2={e.x2} y2={e.y2}
                  stroke={edgeColor(e)}
                  strokeWidth={hoverEdge === e.key ? 2.6 : 1.5}
                  strokeDasharray={e.mode === 'sync' ? undefined : '5 4'}
                  markerEnd="url(#viForceArrow)"
                />
                {/* A fat invisible hit line: a 1.5px stroke is unhoverable. */}
                <line
                  x1={e.x1} y1={e.y1} x2={e.x2} y2={e.y2}
                  stroke="transparent" strokeWidth="12"
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setHoverEdge(e.key)}
                  onMouseLeave={() => setHoverEdge(null)}
                >
                  <title>{`${e.fromId}.${e.signal} → ${e.toId}.${e.slot} · ${e.mode} · ${e.executor}`}</title>
                </line>
              </g>
            ))}
            {shownEdge ? (
              <text
                data-testid="module-graph-edge-label"
                x={shownEdge.mx} y={shownEdge.my - 6} textAnchor="middle"
                fontSize="10" fill={edgeColor(shownEdge)}
                fontFamily="ui-monospace, SFMono-Regular, monospace"
                stroke="#fff" strokeWidth="3.5" paintOrder="stroke"
              >
                {`${shownEdge.label} · ${shownEdge.mode} · ${shownEdge.executor}`}
              </text>
            ) : null}
            {g.nodes.map((n) => {
              const accent = C[n.lane] || C.ui;
              const on = lit(n.id);
              return (
                <g
                  key={n.id}
                  data-node={n.id} data-lane={n.lane}
                  opacity={on ? 1 : 0.18}
                  style={{ cursor: 'pointer' }}
                  onMouseEnter={() => setHover(n.id)}
                  onMouseLeave={() => setHover(null)}
                >
                  <circle
                    cx={n.x} cy={n.y} r={n.r}
                    fill={hover === n.id ? accent : `${accent}33`}
                    stroke={accent}
                    strokeWidth={hover === n.id ? 2.5 : 1.4}
                    strokeDasharray={n.id === 'host' ? '4 3' : undefined}
                  />
                  <text
                    x={n.x} y={n.y + n.r + 11} textAnchor="middle"
                    fontSize={hover === n.id ? 10.5 : 9.5}
                    fontWeight={hover === n.id ? 700 : 500}
                    fill={hover === n.id ? C.ink : C.dim}
                    fontFamily="ui-monospace, SFMono-Regular, monospace"
                    stroke="#fff" strokeWidth="3" paintOrder="stroke"
                    style={{ pointerEvents: 'none' }}
                  >{n.id}</text>
                  <title>{`${n.id} · ${n.type}${n.variant ? ` (${n.variant})` : ''} · ${n.degree} edges`}</title>
                </g>
              );
            })}
          </svg>

          {/* THE PEEK — the hovered module, for real, beside its dot. */}
          {peek ? (
            <div
              data-testid="module-graph-peek"
              style={{
                position: 'absolute',
                left: Math.max(4, Math.min(peek.x + 16, g.width - PEEK_W - 4)),
                top: Math.max(4, Math.min(peek.y + 16, g.height - PEEK_H - 44)),
                width: PEEK_W, pointerEvents: 'none', zIndex: 2,
                background: C.bg, border: `1px solid ${C.line}`, borderRadius: 12,
                boxShadow: '0 8px 24px rgba(0,0,0,0.14)', padding: 8,
              }}
            >
              <MiniModule type={peek.type} />
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, marginTop: 6, minWidth: 0 }}>
                <code style={{ fontSize: 11, fontWeight: 700, color: C.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {peek.id}
                </code>
                <span style={{ fontSize: 9.5, color: C[peek.lane] || C.ui, fontFamily: 'ui-monospace, monospace', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {peek.type}{peek.variant ? ` (${peek.variant})` : ''}
                </span>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div style={{
        display: 'flex', gap: 14, alignItems: 'center', padding: '10px 16px',
        borderTop: `1px solid ${C.line}`, fontSize: 11, color: C.dim, flexWrap: 'wrap',
      }}>
        <span><svg width="24" height="8"><line x1="0" y1="4" x2="24" y2="4" stroke={C.ink} strokeWidth="1.5" /></svg> sync</span>
        <span><svg width="24" height="8"><line x1="0" y1="4" x2="24" y2="4" stroke={C.ink} strokeWidth="1.5" strokeDasharray="5 4" /></svg> async</span>
        <span><svg width="24" height="8"><line x1="0" y1="4" x2="24" y2="4" stroke={C.soft} strokeWidth="1" /></svg> contains</span>
        <span style={{ color: C.ui }}>● ui</span>
        <span style={{ color: C.data }}>● data</span>
        <span style={{ color: C.host }}>● host (dashed = no UI)</span>
        <span style={{ marginLeft: 'auto' }}>
          {g.nodes.length} modules · {g.connections} connections · hover a dot for the module, a wire for its payload
        </span>
      </div>
    </div>
  );
}
